<template>
  <div>
    <h1>Teacher Analytics</h1>
    <p class="text-muted font-weight-bold">
      Monitor teacher and class performance across the school
    </p>
    <hr />

    <!-- Filters -->
    <b-row class="mb-2">
      <b-col md="3" sm="6">
        <v-select
          v-model="selectedSubject"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          label="label"
          :options="['All Subjects', 'Math', 'ELA', 'Science']"
          :clearable="false"
          class="bg-white"
        />
      </b-col>
      <b-col md="3" sm="6">
        <v-select
          v-model="classID"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          label="name"
          :options="classesOptions"
          :reduce="(opt) => opt.id"
          :clearable="false"
          placeholder="Select class"
          class="bg-white"
        />
      </b-col>
    </b-row>

    <!-- Comparison Chart -->
    <b-card no-body class="mb-2 shadow-sm">
      <b-card-header>
        <b-card-title>Subject Performance: School vs District</b-card-title>
      </b-card-header>
      <b-card-body>
        <vue-apex-charts
          type="bar"
          height="300"
          :options="comparisonChartOptions"
          :series="comparisonChartSeries"
        />
      </b-card-body>
    </b-card>

    <!-- Teacher Cards -->
    <b-row>
      <b-col md="6" v-for="(teacher, index) in teachers" :key="index">
        <b-card no-body class="mb-2 shadow-sm">
          <b-card-body>
            <!-- Header -->
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="42"
                  variant="light-secondary"
                  :text="teacher.initials"
                  class="mr-1"
                />
                <div>
                  <h5 class="mb-0 font-weight-bold text-dark">
                    {{ teacher.name }}
                  </h5>
                  <small>{{ teacher.subject }}</small>
                </div>
              </div>
              <b-button variant="flat-secondary" class="btn-icon">
                <feather-icon icon="MailIcon" size="16" />
              </b-button>
            </div>

            <!-- Stats Grid -->
            <b-row class="text-center mb-2">
              <b-col cols="4" class="px-1 border-right">
                <div class="bg-light rounded py-1">
                  <small class="d-block mb-50">Classes</small>
                  <h5 class="font-weight-bolder mb-0">{{ teacher.classes }}</h5>
                </div>
              </b-col>
              <b-col cols="4" class="px-1 border-right">
                <div class="bg-light rounded py-1">
                  <small class="d-block mb-50">Students</small>
                  <h5 class="font-weight-bolder mb-0">
                    {{ teacher.students }}
                  </h5>
                </div>
              </b-col>
              <b-col cols="4" class="px-1">
                <div class="bg-light rounded py-1">
                  <small class="d-block mb-50">Avg Score</small>
                  <h5 class="font-weight-bolder mb-0">
                    {{ teacher.avgScore }}%
                  </h5>
                </div>
              </b-col>
            </b-row>

            <!-- Proficiency Progress -->
            <div class="mb-2">
              <div class="d-flex justify-content-between mb-50">
                <small class="text-muted">Proficiency Rate</small>
                <small class="font-weight-bold"
                  >{{ teacher.proficiency }}%</small
                >
              </div>
              <b-progress
                :value="teacher.proficiency"
                max="100"
                height="6px"
                variant="dark"
              ></b-progress>
            </div>

            <!-- Footer -->
            <div class="d-flex justify-content-between align-items-center">
              <div class="text-success small font-weight-bold">
                <feather-icon icon="TrendingUpIcon" size="14" class="mr-50" />
                <span>{{ teacher.growth }} growth</span>
              </div>
              <b-badge
                pill
                :variant="
                  teacher.status === 'On Track' ? 'dark' : 'light-secondary'
                "
                class="px-2 py-50"
              >
                {{ teacher.status }}
              </b-badge>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BAvatar,
  BProgress,
  BBadge,
  BButton,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import vSelect from "vue-select";

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BAvatar,
    BProgress,
    BBadge,
    BButton,
    VueApexCharts,
    vSelect,
  },
  data() {
    return {
      classID: 0,
      selectedSubject: "All Subjects",
      classesOptions: [
        { id: 0, name: "All Grades" },
        { id: 1, name: "Grade 1" },
        { id: 2, name: "Grade 2" },
        { id: 3, name: "Grade 3" },
      ],
      // Comparison Chart Data
      comparisonChartSeries: [
        {
          name: "School",
          data: [75, 78, 71, 68],
        },
        {
          name: "District",
          data: [72, 74, 70, 69],
        },
      ],
      comparisonChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        colors: ["#ff9f43", "#00cfe8"],
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "50%",
            endingShape: "rounded",
          },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: ["Math", "ELA", "Science", "Social St"],
          axisBorder: { show: false },
          axisTicks: { show: false },
          max: 100,
        },
        grid: {
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      },
      // Teacher Data
      teachers: [
        {
          name: "Sarah Johnson",
          initials: "SJ",
          subject: "Mathematics",
          classes: 4,
          students: 96,
          avgScore: 78.5,
          proficiency: 74,
          growth: "+8.2%",
          status: "Needs Support",
        },
        {
          name: "Michael Chen",
          initials: "MC",
          subject: "English Language Arts",
          classes: 3,
          students: 72,
          avgScore: 81.2,
          proficiency: 79,
          growth: "+5.4%",
          status: "On Track",
        },
        {
          name: "Emily Davis",
          initials: "ED",
          subject: "Science",
          classes: 5,
          students: 110,
          avgScore: 83.4,
          proficiency: 85,
          growth: "+6.1%",
          status: "On Track",
        },
        {
          name: "David Wilson",
          initials: "DW",
          subject: "Social Studies",
          classes: 4,
          students: 92,
          avgScore: 75.8,
          proficiency: 70,
          growth: "+3.5%",
          status: "Needs Support",
        },
        {
          name: "Jessica Martinez",
          initials: "JM",
          subject: "Mathematics",
          classes: 3,
          students: 68,
          avgScore: 88.0,
          proficiency: 92,
          growth: "+9.5%",
          status: "On Track",
        },
      ],
    };
  },
};
</script>

<style scoped>
.text-success {
  color: #28c76f !important;
}
</style>
